import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FiShield, FiEye, FiDownload, FiMapPin, FiCalendar, FiFileText, 
  FiThermometer, FiDroplet, FiCheckCircle,
  FiLayers, FiClock
} from 'react-icons/fi';

const TransparencyContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #a0a0a0;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProofOfReserveSection = styled.section`
  background: rgba(0, 255, 136, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
`;

const ReserveTitle = styled.h2`
  font-size: 2rem;
  color: #00ff88;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ReserveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const ReserveCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  
  .metric-value {
    font-size: 2.5rem;
    font-weight: 700;
    color: #00ff88;
    margin-bottom: 0.5rem;
  }
  
  .metric-label {
    color: #a0a0a0;
    font-size: 1rem;
    margin-bottom: 1rem;
  }
  
  .metric-detail {
    color: #fff;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 3rem;
`;

const TabHeader = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TabButton = styled.button`
  padding: 1rem 2rem;
  background: ${props => props.active ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'transparent'};
  color: ${props => props.active ? '#000' : '#a0a0a0'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#4facfe' : 'transparent'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.active ? '#000' : '#4facfe'};
  }
`;

const TabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const WarehouseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const WarehouseCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #4facfe;
    transform: translateY(-3px);
  }
`;

const WarehouseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  .location {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #4facfe;
    font-weight: 600;
    font-size: 1.1rem;
  }
  
  .status {
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    background: rgba(0, 255, 136, 0.1);
    color: #00ff88;
    border: 1px solid rgba(0, 255, 136, 0.3);
  }
`;

const WarehouseMetrics = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  .metric {
    .label {
      font-size: 0.9rem;
      color: #a0a0a0;
      margin-bottom: 0.3rem;
    }
    
    .value {
      font-size: 1.1rem;
      font-weight: 600;
      color: #fff;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
`;

const CertificationSection = styled.div`
  background: rgba(79, 172, 254, 0.05);
  border: 1px solid rgba(79, 172, 254, 0.2);
  border-radius: 10px;
  padding: 1.5rem;
  margin-top: 1rem;
  
  .cert-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    .cert-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #4facfe;
    }
    
    .cert-date {
      font-size: 0.9rem;
      color: #a0a0a0;
    }
  }
  
  .cert-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    font-size: 0.9rem;
    color: #a0a0a0;
  }
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const DocumentCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #4facfe;
    transform: translateY(-2px);
  }
  
  .doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    .doc-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #fff;
    }
    
    .doc-status {
      padding: 0.2rem 0.6rem;
      border-radius: 15px;
      font-size: 0.8rem;
      font-weight: 600;
      background: rgba(0, 255, 136, 0.1);
      color: #00ff88;
    }
  }
  
  .doc-details {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
  
  .doc-actions {
    display: flex;
    gap: 0.5rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.primary ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'transparent'};
  color: ${props => props.primary ? '#000' : '#4facfe'};
  border: 1px solid #4facfe;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  
  &:hover {
    background: ${props => props.primary ? 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)' : 'rgba(79, 172, 254, 0.1)'};
  }
`;

const TrackingTimeline = styled.div`
  position: relative;
  padding-left: 2rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 0.75rem;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, #4facfe, #00f2fe);
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 2rem;
  
  &::before {
    content: '';
    position: absolute;
    left: -1.75rem;
    top: 0.5rem;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.verified ? '#00ff88' : '#4facfe'};
    border: 2px solid #1a1a3e;
  }
  
  .timeline-date {
    font-size: 0.9rem;
    color: #4facfe;
    font-weight: 600;
    margin-bottom: 0.3rem;
  }
  
  .timeline-title {
    font-size: 1.1rem;
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  .timeline-desc {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 0.5rem;
  }
  
  .timeline-hash {
    font-family: monospace;
    font-size: 0.8rem;
    color: #4facfe;
    background: rgba(79, 172, 254, 0.1);
    padding: 0.3rem 0.6rem;
    border-radius: 6px;
    display: inline-block;
  }
`;

const LiveFeedSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  
  .feed-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    
    h3 {
      color: #fff;
      font-size: 1.3rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    
    .live-indicator {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #00ff88;
      font-size: 0.9rem;
      
      &::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #00ff88;
        animation: pulse 2s infinite;
      }
    }
  }
`;

const FeedItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
  
  .feed-content {
    .feed-action {
      color: #fff;
      font-weight: 600;
      margin-bottom: 0.3rem;
    }
    
    .feed-details {
      color: #a0a0a0;
      font-size: 0.9rem;
    }
  }
  
  .feed-time {
    color: #4facfe;
    font-size: 0.9rem;
    font-weight: 600;
  }
`;

function TransparencyHub() {
  const [activeTab, setActiveTab] = useState('reserves');
  const [liveData, setLiveData] = useState({
    totalReserves: 250000,
    totalTokens: 250000,
    backingRatio: 100,
    lastAudit: new Date().toISOString(),
    activeWarehouses: 3,
    totalBatches: 35
  });

  const warehouses = [
    {
      id: 'dubai-dmcc',
      name: 'Dubai DMCC Warehouse A',
      location: 'Dubai, UAE',
      totalKg: 85000,
      batches: 12,
      temperature: 22,
      humidity: 45,
      lastInspection: '2024-01-15',
      status: 'Verified',
      icumsaRating: 45,
      certifications: ['DMCC Approved', 'ISO 22000', 'HACCP'],
      auditFirm: 'Bureau Veritas',
      insuranceValue: '$55,250'
    },
    {
      id: 'singapore-port',
      name: 'Singapore Port Storage',
      location: 'Singapore',
      totalKg: 95000,
      batches: 15,
      temperature: 21,
      humidity: 42,
      lastInspection: '2024-01-18',
      status: 'Verified',
      icumsaRating: 45,
      certifications: ['PSA Certified', 'ISO 22000', 'FSSC 22000'],
      auditFirm: 'SGS',
      insuranceValue: '$61,750'
    },
    {
      id: 'swiss-vault',
      name: 'Swiss Commodity Vault',
      location: 'Zurich, Switzerland',
      totalKg: 70000,
      batches: 8,
      temperature: 20,
      humidity: 38,
      lastInspection: '2024-01-20',
      status: 'Verified',
      icumsaRating: 30,
      certifications: ['Swiss Customs', 'ISO 22000', 'BRC'],
      auditFirm: 'KPMG',
      insuranceValue: '$45,500'
    }
  ];

  const documents = [
    {
      title: 'Q4 2023 Audit Report',
      type: 'Audit',
      date: '2024-01-15',
      auditor: 'Bureau Veritas',
      status: 'Verified',
      description: 'Comprehensive quarterly audit of all sugar inventory across global warehouses.',
      fileSize: '2.3 MB'
    },
    {
      title: 'ICUMSA 45 Quality Certificate',
      type: 'Quality',
      date: '2024-01-10',
      auditor: 'International Commission',
      status: 'Valid',
      description: 'Official ICUMSA rating certification for sugar quality standards.',
      fileSize: '1.1 MB'
    },
    {
      title: 'Insurance Coverage Policy',
      type: 'Insurance',
      date: '2024-01-01',
      auditor: 'Lloyd\'s of London',
      status: 'Active',
      description: 'Comprehensive insurance coverage for all physical sugar assets.',
      fileSize: '890 KB'
    },
    {
      title: 'Dubai DMCC Storage License',
      type: 'License',
      date: '2023-12-01',
      auditor: 'DMCC Authority',
      status: 'Valid',
      description: 'Official storage and handling license for Dubai warehouse operations.',
      fileSize: '1.5 MB'
    }
  ];

  const blockchainEvents = [
    {
      date: '2024-01-22 14:30 UTC',
      title: 'Asset Audit Completed',
      description: 'Quarterly audit completed for all 3 warehouses. Total verified: 250,000 kg',
      hash: 'cba7f8...92c3',
      verified: true
    },
    {
      date: '2024-01-22 09:15 UTC',
      title: 'Temperature Monitoring Update',
      description: 'Automated IoT sensors reported optimal storage conditions across all facilities',
      hash: 'cb3b4e...71f9',
      verified: true
    },
    {
      date: '2024-01-21 16:45 UTC',
      title: 'Batch SGR-2024-089 Allocated',
      description: 'New 5,000 kg batch allocated to Singapore warehouse with ICUMSA 45 rating',
      hash: 'cb9c2d...48a7',
      verified: true
    },
    {
      date: '2024-01-21 11:20 UTC',
      title: 'Insurance Policy Renewed',
      description: 'Annual insurance coverage renewed with Lloyd\'s of London for $162.5M',
      hash: 'cb5f8a...61e2',
      verified: true
    }
  ];

  const liveFeed = [
    {
      action: 'Quality Check Completed',
      details: 'ICUMSA rating verified for Batch SGR-2024-091',
      time: '2 minutes ago'
    },
    {
      action: 'Temperature Alert Resolved',
      details: 'Dubai warehouse temperature normalized to 22°C',
      time: '15 minutes ago'
    },
    {
      action: 'New Audit Scheduled',
      details: 'Monthly inspection scheduled for Singapore facility',
      time: '1 hour ago'
    },
    {
      action: 'Token Redemption Processed',
      details: '500 kg delivered to verified customer in Dubai',
      time: '3 hours ago'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData(prev => ({
        ...prev,
        backingRatio: 100 + (Math.random() - 0.5) * 0.1 // Small variation around 100%
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const renderReservesTab = () => (
    <TabContent active={activeTab === 'reserves'}>
      <ProofOfReserveSection>
        <ReserveTitle>
          <FiShield />
          Real-Time Proof of Reserve
        </ReserveTitle>
        
        <ReserveGrid>
          <ReserveCard>
            <div className="metric-value">{liveData.totalReserves.toLocaleString()} kg</div>
            <div className="metric-label">Physical Sugar Reserves</div>
            <div className="metric-detail">
              <FiMapPin />
              Across {liveData.activeWarehouses} global warehouses
            </div>
          </ReserveCard>
          
          <ReserveCard>
            <div className="metric-value">{liveData.totalTokens.toLocaleString()}</div>
            <div className="metric-label">Sugar Tokens in Circulation</div>
            <div className="metric-detail">
              <FiLayers />
              1 token = 1 kg sugar
            </div>
          </ReserveCard>
          
          <ReserveCard>
            <div className="metric-value">{liveData.backingRatio.toFixed(2)}%</div>
            <div className="metric-label">Reserve Backing Ratio</div>
            <div className="metric-detail">
              <FiCheckCircle />
              Fully collateralized
            </div>
          </ReserveCard>
          
          <ReserveCard>
            <div className="metric-value">{liveData.totalBatches}</div>
            <div className="metric-label">Active Storage Batches</div>
            <div className="metric-detail">
              <FiCalendar />
              Last verified: {new Date().toLocaleDateString()}
            </div>
          </ReserveCard>
        </ReserveGrid>
      </ProofOfReserveSection>

      <WarehouseGrid>
        {warehouses.map(warehouse => (
          <WarehouseCard key={warehouse.id}>
            <WarehouseHeader>
              <div className="location">
                <FiMapPin />
                {warehouse.name}
              </div>
              <div className="status">{warehouse.status}</div>
            </WarehouseHeader>
            
            <WarehouseMetrics>
              <div className="metric">
                <div className="label">Total Sugar</div>
                <div className="value">{warehouse.totalKg.toLocaleString()} kg</div>
              </div>
              <div className="metric">
                <div className="label">Storage Batches</div>
                <div className="value">{warehouse.batches} lots</div>
              </div>
              <div className="metric">
                <div className="label">Temperature</div>
                <div className="value">
                  <FiThermometer />
                  {warehouse.temperature}°C
                </div>
              </div>
              <div className="metric">
                <div className="label">Humidity</div>
                <div className="value">
                  <FiDroplet />
                  {warehouse.humidity}%
                </div>
              </div>
            </WarehouseMetrics>

            <CertificationSection>
              <div className="cert-header">
                <div className="cert-title">ICUMSA {warehouse.icumsaRating} Certified</div>
                <div className="cert-date">{warehouse.lastInspection}</div>
              </div>
              <div className="cert-details">
                <div>Auditor: {warehouse.auditFirm}</div>
                <div>Insurance: {warehouse.insuranceValue}</div>
              </div>
            </CertificationSection>
          </WarehouseCard>
        ))}
      </WarehouseGrid>
    </TabContent>
  );

  const renderDocumentsTab = () => (
    <TabContent active={activeTab === 'documents'}>
      <h3 style={{ color: '#fff', marginBottom: '2rem', fontSize: '1.5rem' }}>
        Official Documentation & Certifications
      </h3>
      
      <DocumentsGrid>
        {documents.map((doc, index) => (
          <DocumentCard key={index}>
            <div className="doc-header">
              <div className="doc-title">{doc.title}</div>
              <div className="doc-status">{doc.status}</div>
            </div>
            
            <div className="doc-details">
              <p><strong>Type:</strong> {doc.type}</p>
              <p><strong>Date:</strong> {doc.date}</p>
              <p><strong>Issued by:</strong> {doc.auditor}</p>
              <p><strong>Size:</strong> {doc.fileSize}</p>
              <p style={{ marginTop: '0.5rem' }}>{doc.description}</p>
            </div>
            
            <div className="doc-actions">
              <ActionButton primary>
                <FiDownload />
                Download
              </ActionButton>
              <ActionButton>
                <FiEye />
                Preview
              </ActionButton>
            </div>
          </DocumentCard>
        ))}
      </DocumentsGrid>
    </TabContent>
  );

  const renderTrackingTab = () => (
    <TabContent active={activeTab === 'tracking'}>
      <h3 style={{ color: '#fff', marginBottom: '2rem', fontSize: '1.5rem' }}>
        Blockchain Event Timeline
      </h3>
      
      <TrackingTimeline>
        {blockchainEvents.map((event, index) => (
          <TimelineItem key={index} verified={event.verified}>
            <div className="timeline-date">{event.date}</div>
            <div className="timeline-title">{event.title}</div>
            <div className="timeline-desc">{event.description}</div>
            <div className="timeline-hash">Hash: {event.hash}</div>
          </TimelineItem>
        ))}
      </TrackingTimeline>
    </TabContent>
  );

  const renderLiveFeedTab = () => (
    <TabContent active={activeTab === 'live'}>
      <LiveFeedSection>
        <div className="feed-header">
          <h3>
            <FiClock />
            Live Activity Feed
          </h3>
          <div className="live-indicator">
            Live Updates
          </div>
        </div>
        
        {liveFeed.map((item, index) => (
          <FeedItem key={index}>
            <div className="feed-content">
              <div className="feed-action">{item.action}</div>
              <div className="feed-details">{item.details}</div>
            </div>
            <div className="feed-time">{item.time}</div>
          </FeedItem>
        ))}
      </LiveFeedSection>
    </TabContent>
  );

  return (
    <TransparencyContainer>
      <Header>
        <Title>Transparency Hub</Title>
        <Subtitle>
          Real-time verification of sugar token backing with comprehensive audits, 
          quality certifications, and blockchain-tracked asset management. 
          Complete transparency in every gram of sugar.
        </Subtitle>
      </Header>

      <TabContainer>
        <TabHeader>
          <TabButton 
            active={activeTab === 'reserves'} 
            onClick={() => setActiveTab('reserves')}
          >
            <FiShield style={{ marginRight: '0.5rem' }} />
            Proof of Reserve
          </TabButton>
          <TabButton 
            active={activeTab === 'documents'} 
            onClick={() => setActiveTab('documents')}
          >
            <FiFileText style={{ marginRight: '0.5rem' }} />
            Documents & Audits
          </TabButton>
          <TabButton 
            active={activeTab === 'tracking'} 
            onClick={() => setActiveTab('tracking')}
          >
            <FiLayers style={{ marginRight: '0.5rem' }} />
            Blockchain Tracking
          </TabButton>
          <TabButton 
            active={activeTab === 'live'} 
            onClick={() => setActiveTab('live')}
          >
            <FiClock style={{ marginRight: '0.5rem' }} />
            Live Feed
          </TabButton>
        </TabHeader>

        {renderReservesTab()}
        {renderDocumentsTab()}
        {renderTrackingTab()}
        {renderLiveFeedTab()}
      </TabContainer>
    </TransparencyContainer>
  );
}

export default TransparencyHub; 